import React, { useState } from 'react';
import { Role } from '../../../types/user';
import { getMenuItems } from '../../layout/sidebar/menuItems';
import { useLanguage } from '../../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Wallet, 
  Scale, 
  Laptop2, 
  ShieldCheck, 
  ClipboardList, 
  Database, 
  Users,
  Building2,
  Briefcase,
  UserCog,
  Brain,
  LineChart,
  Settings,
  FileText,
  UserCheck,
  Network,
  Globe,
  BookOpen,
  Microscope,
  Lightbulb,
  Target,
  Search
} from 'lucide-react';

// Define available role icons with categories
const roleIconCategories = [
  {
    name: 'Management',
    icons: [
      { icon: ShieldCheck, label: 'Admin' },
      { icon: ClipboardList, label: 'Operations Manager' },
      { icon: Target, label: 'Project Manager' },
      { icon: Brain, label: 'Strategic Planner' },
      { icon: Briefcase, label: 'HR Manager' }
    ]
  },
  {
    name: 'Technical',
    icons: [
      { icon: Laptop2, label: 'IT Manager' },
      { icon: Database, label: 'Data Manager' },
      { icon: Network, label: 'Network Manager' },
      { icon: UserCog, label: 'System Administrator' },
      { icon: Settings, label: 'Technical Supervisor' }
    ]
  },
  {
    name: 'Business',
    icons: [
      { icon: Wallet, label: 'Financial Manager' },
      { icon: Users, label: 'Organizers Supervisor' },
      { icon: Scale, label: 'Dispute Auditor' },
      { icon: LineChart, label: 'Analytics Manager' },
      { icon: Globe, label: 'International Relations' }
    ]
  },
  {
    name: 'Support',
    icons: [
      { icon: Building2, label: 'Facilities Manager' },
      { icon: FileText, label: 'Documentation Manager' },
      { icon: UserCheck, label: 'Compliance Officer' },
      { icon: BookOpen, label: 'Training Manager' },
      { icon: Microscope, label: 'Quality Assurance' },
      { icon: Lightbulb, label: 'Innovation Manager' }
    ]
  }
];

type Props = {
  onSubmit: (data: Partial<Role>) => void;
  initialData?: Partial<Role>;
  isLoading?: boolean;
};

export const RoleForm = ({ onSubmit, initialData = {}, isLoading }: Props) => {
  const { translations } = useLanguage();
  const allMenuItems = getMenuItems('admin');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    menuItems: initialData.menuItems || {},
    color: initialData.color || '#00897B',
    icon: initialData.icon || 'Admin'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize menu items permissions
  React.useEffect(() => {
    if (!formData.menuItems || Object.keys(formData.menuItems).length === 0) {
      const initialMenuItems = allMenuItems.reduce((acc, item) => {
        acc[item.path] = true;
        return acc;
      }, {});
      setFormData(prev => ({ ...prev, menuItems: initialMenuItems }));
    }
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = translations?.roles?.form?.validation?.required || 'This field is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleMenuItemToggle = (path: string) => {
    setFormData(prev => ({
      ...prev,
      menuItems: {
        ...prev.menuItems,
        [path]: !prev.menuItems[path]
      }
    }));
  };

  // Filter icons based on search
  const filteredCategories = roleIconCategories.map(category => ({
    ...category,
    icons: category.icons.filter(({ label }) => 
      label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.icons.length > 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Role Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {translations?.roles?.form?.fields?.name || 'Role Name'}
          <span className="text-red-500 mr-1">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-500">{errors.name}</p>
        )}
      </div>

      {/* Icon Selection */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Role Icon
        </label>
        
        {/* Search Icons */}
        <div className="relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search icons..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>

        {/* Icon Categories */}
        <div className="space-y-6">
          {filteredCategories.map((category) => (
            <div key={category.name}>
              <h3 className="text-sm font-medium text-gray-500 mb-3">{category.name}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {category.icons.map(({ icon: Icon, label }) => (
                  <motion.button
                    key={label}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: label })}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                      formData.icon === label
                        ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                        : 'border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        formData.icon === label
                          ? 'bg-primary-100'
                          : 'bg-gray-100'
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                  </motion.button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Color Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Role Color
        </label>
        <div className="flex items-center gap-4">
          <input
            type="color"
            value={formData.color}
            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            className="w-20 h-20 rounded-lg cursor-pointer border-0"
          />
          <div className="flex-1 space-y-2">
            <div
              className="h-10 rounded-lg"
              style={{ backgroundColor: formData.color }}
            />
            <div
              className="h-10 rounded-lg"
              style={{ backgroundColor: `${formData.color}15` }}
            />
          </div>
        </div>
      </div>

      {/* Menu Items Permissions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Menu Items Visibility
        </label>
        
        <div className="space-y-4">
          {Object.entries(getMenuItems('admin').reduce((acc, item) => {
            if (!acc[item.group]) {
              acc[item.group] = [];
            }
            acc[item.group].push(item);
            return acc;
          }, {})).map(([group, items]) => (
            <div key={group} className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-3 capitalize">{group}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(items as any[]).map((item) => (
                  <label
                    key={item.path}
                    className="flex items-center justify-between p-2 bg-white rounded border hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <item.icon size={16} className="text-gray-500" />
                      <span className="text-sm text-gray-600">{item.label}</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.menuItems[item.path] ?? true}
                      onChange={() => handleMenuItemToggle(item.path)}
                      className="rounded text-primary-500 focus:ring-primary-500"
                    />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-3">
        <motion.button
          type="submit"
          disabled={isLoading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={`px-6 py-2 rounded-lg text-white transition-colors ${
            isLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {isLoading ? translations?.roles?.form?.buttons?.saving : translations?.roles?.form?.buttons?.save}
        </motion.button>
      </div>
    </form>
  );
};